import { Controller } from "@hotwired/stimulus"
import * as pdfjs from "pdfjs-dist"

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3]

export default class extends Controller {
  static values = { url: String, workerSrc: String }
  static targets = ["pages", "loading", "scroller", "page", "total", "zoom", "zoomControl", "download"]

  async connect() {
    this.zoom = 1
    pdfjs.GlobalWorkerOptions.workerSrc = this.workerSrcValue
    try {
      // Fetching the whole document disables range requests, so the presigned
      // URL behind the redirect only needs to survive this initial fetch.
      const response = await fetch(this.urlValue)
      if (!response.ok) throw new Error(`Report download failed (${response.status})`)

      const data = await response.arrayBuffer()
      this.pdf = await pdfjs.getDocument({ data }).promise
      this.loadingTarget.hidden = true
      this.pageTarget.textContent = 1
      this.totalTarget.textContent = this.pdf.numPages
      await this.render()
      this.zoomControlTargets.forEach(button => { button.disabled = false })
    } catch (error) {
      this.loadingTarget.hidden = false
      this.loadingTarget.setAttribute('role', 'alert')
      this.loadingTarget.textContent = 'This report couldn’t load. Try reloading the page, or use Download to open the PDF directly.'
      throw error
    }
  }

  // Cmd/Ctrl+S saves the actual PDF instead of the HTML page
  download(event) {
    event.preventDefault()
    this.downloadTarget.click()
  }

  zoomIn() {
    this.setZoom(ZOOM_STEPS.find((step) => step > this.zoom))
  }

  zoomOut() {
    this.setZoom([...ZOOM_STEPS].reverse().find((step) => step < this.zoom))
  }

  setZoom(zoom) {
    if (!zoom) return
    this.zoom = zoom
    this.render()
  }

  updatePage() {
    const midpoint = this.scrollerTarget.scrollTop + this.scrollerTarget.clientHeight / 2
    let current = 1
    for (const [index, canvas] of [...this.pagesTarget.children].entries()) {
      if (canvas.offsetTop <= midpoint) current = index + 1
    }
    this.pageTarget.textContent = current
  }

  async render() {
    const token = (this.renderToken = {})
    this.zoomTarget.textContent = `${Math.round(this.zoom * 100)}%`
    this.pagesTarget.replaceChildren()
    for (let i = 1; i <= this.pdf.numPages; i++) {
      if (token !== this.renderToken) return
      await this.renderPage(await this.pdf.getPage(i))
    }
  }

  async renderPage(page) {
    const fitWidth = Math.min(this.scrollerTarget.clientWidth - 32, 1000)
    const width = fitWidth * this.zoom
    const outputScale = window.devicePixelRatio || 1
    const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width })
    const canvas = document.createElement("canvas")
    canvas.width = Math.floor(viewport.width * outputScale)
    canvas.height = Math.floor(viewport.height * outputScale)
    canvas.style.width = `${Math.floor(viewport.width)}px`
    canvas.className = "bg-white shadow-lg"
    this.pagesTarget.appendChild(canvas)
    await page.render({
      canvasContext: canvas.getContext("2d"),
      transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
      viewport
    }).promise
  }
}
